/*
  Component: Promotion countdown
  Responsibility: count down to the moment a campaign ends, and take the line away once it
                  has passed.

  The target comes from the metaobject's promotion_count_down_date, which is a Single line
  text field rather than a date one — nothing in the admin validates it. So the parse is
  guarded and anything unreadable removes the line rather than rendering NaN at a visitor.
  The markup carries the raw value as its text until this runs, which is why a browser that
  never runs it shows a date instead of an empty row.

  setInterval here is the thing being measured, not a wait for the DOM — the seconds have to
  keep moving. It is cleared the moment the campaign ends, so nothing ticks forever.

  Prefixed constants: every authored script shares one global scope, so a bare SECOND here
  would collide with the same name in another kl-*.js and kill whichever loads second.
*/

const KL_PROMOTION_COUNTDOWN_SECOND = 1000;
const KL_PROMOTION_COUNTDOWN_MINUTE = KL_PROMOTION_COUNTDOWN_SECOND * 60;
const KL_PROMOTION_COUNTDOWN_HOUR = KL_PROMOTION_COUNTDOWN_MINUTE * 60;
const KL_PROMOTION_COUNTDOWN_DAY = KL_PROMOTION_COUNTDOWN_HOUR * 24;

/*
  Pure: milliseconds in, the four parts out. DOM-free so the arithmetic can be read and
  checked without a browser — which matters more here than usual, because an off-by-one in a
  remainder is invisible until a campaign is a day from ending.
*/
function getCountdownParts(remainingMs) {
  return {
    days: Math.floor(remainingMs / KL_PROMOTION_COUNTDOWN_DAY),
    hours: Math.floor((remainingMs % KL_PROMOTION_COUNTDOWN_DAY) / KL_PROMOTION_COUNTDOWN_HOUR),
    minutes: Math.floor((remainingMs % KL_PROMOTION_COUNTDOWN_HOUR) / KL_PROMOTION_COUNTDOWN_MINUTE),
    seconds: Math.floor((remainingMs % KL_PROMOTION_COUNTDOWN_MINUTE) / KL_PROMOTION_COUNTDOWN_SECOND),
  };
}

/* Pure. Keep values and units separate so they can use different font weights. */
function formatCountdown(parts) {
  return `<span class="kl-promotion-countdown__value">${parts.days}</span><span class="kl-promotion-countdown__unit">d</span> : <span class="kl-promotion-countdown__value">${parts.hours}</span><span class="kl-promotion-countdown__unit">h</span> : <span class="kl-promotion-countdown__value">${parts.minutes}</span><span class="kl-promotion-countdown__unit">m</span> : <span class="kl-promotion-countdown__value">${parts.seconds}</span><span class="kl-promotion-countdown__unit">s</span>`;
}

function initPromotionCountdown(countdownElement) {
  const timeElement = countdownElement.querySelector('[data-kl-promotion-countdown-time]');
  const targetValue = countdownElement.dataset.klPromotionCountdownTarget;

  if (!timeElement || !targetValue) return;

  const targetTime = Date.parse(targetValue);

  if (Number.isNaN(targetTime)) {
    countdownElement.hidden = true;
    console.debug('kl-promotion-countdown: unreadable end date', { targetValue });
    return;
  }

  let intervalId = null;

  function render() {
    const remainingMs = targetTime - Date.now();

    /*
      Past its end the campaign is over, and a line saying so is worse than no line: the
      chip and the price beside it are still showing the offer, so the honest thing is to
      stop claiming a deadline that has gone.
    */
    if (remainingMs <= 0) {
      countdownElement.hidden = true;

      if (intervalId) clearInterval(intervalId);

      return;
    }

    timeElement.innerHTML = formatCountdown(getCountdownParts(remainingMs));

    /*
      Shown only once it has something true to say. The markup renders hidden precisely so
      that nobody reads a placeholder while this script is on its way — see the note in
      snippets/kl-promotion.liquid. Setting it every tick rather than once is free and means
      the line comes back by itself if anything else ever hides it.
    */
    countdownElement.hidden = false;
  }

  render();

  if (!countdownElement.hidden) {
    intervalId = setInterval(render, KL_PROMOTION_COUNTDOWN_SECOND);
  }
}

document.querySelectorAll('[data-kl-promotion-countdown]').forEach(initPromotionCountdown);

// Theme editor: a re-rendered section arrives with fresh, uninitialised markup.
document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll('[data-kl-promotion-countdown]').forEach(initPromotionCountdown);
});
